<!-- /news/2026, /news/2025… (/news redirects to the current year): emails sent to the full
     VIZBI list and the old blog posts, that year only, newest first. Each item expands in place
     (the first starts expanded) and has its own page, /news/2026/04-30. -->

<template>
  <NewsLayout>
    <div class="top-line">
      <!-- breadcrumb: News / 2026; the current page (the year) is green -->
      <h3 class="breadcrumb news-heading">
        <router-link to="/news">News</router-link>
        <span class="separator">/</span>
        <span class="crumb-year" aria-current="page">{{ year }}</span>
      </h3>
      <div class="actions">
        <!-- narrow screens: the years are a full-screen picker (wider ones: the Year column) -->
        <NewsYearPicker v-if="isNarrow" :year="year" />
        <!-- always shown: collapses everything while any item is open, else expands them all -->
        <!-- the email updates at hand (2026-10-23, Seán's mockup): #subscribe -->
        <NewsSubscribeButton />
        <button class="toggle-all" type="button" @click="toggleAll">
          {{ expandedSlugs.size ? "Collapse all" : "Expand all" }}
        </button>
      </div>
    </div>
    <section v-for="month in months" :id="`month-${month.key}`" :key="month.key">
      <NewsRow
        v-for="item in month.items"
        :key="item.slug"
        :item="item"
        :item-path="getNewsItemPath(item, items)"
        :is-expanded="expandedSlugs.has(item.slug)"
        @toggle="toggle(item.slug)"
      />
    </section>
  </NewsLayout>
</template>

<script setup>
import {computed, inject, reactive, ref} from "vue";
import {useRoute} from "vue-router";
import NewsLayout from "@/components/News/Layout.vue";
import NewsRow from "@/components/News/Row.vue";
import NewsYearPicker from "@/components/News/YearPicker.vue";
import NewsSubscribeButton from "@/components/News/SubscribeButton.vue";
import {fetchNewsYear, getNewsItemPath, getNewsMonths} from "@/composables/useNews";

const route = useRoute();
// fixed per page: each path (/news/2026, /news/2025…) is its own kept-alive page, and the route
// changes under it (e.g. to an item page) must not reload it as another year
const year = route.params.year;
const displayWidth = inject("displayWidth");
const isNarrow = computed(() => displayWidth.value === "narrow"); // years: a full-screen picker
const items = ref([]);
const months = computed(() => getNewsMonths(items.value));
const expandedSlugs = reactive(new Set());

function toggle(slug) {
  if (expandedSlugs.has(slug)) expandedSlugs.delete(slug);
  else expandedSlugs.add(slug);
}

function toggleAll() {
  if (expandedSlugs.size) return expandedSlugs.clear();
  for (const item of items.value) expandedSlugs.add(item.slug);
}

async function loadYear() {
  items.value = await fetchNewsYear(year);
  if (items.value[0]) expandedSlugs.add(items.value[0].slug); // the first starts expanded
}

loadYear();
</script>

<style scoped>
/* links as the year navigation on /2026 (Navigation/Secondary.vue): grey, never underlined,
   lighter on hover; the current page in brand green */
.breadcrumb a,
.breadcrumb a:hover {
  text-decoration: none;
}
.breadcrumb a {
  color: var(--text-color-subtle);
}
.breadcrumb a:hover {
  color: var(--text-color-less-subtle);
}
.separator {
  color: var(--text-color-subtle); /* as the links: the divider on /2026 is this grey */
  font-weight: 400;
}
/* the current page */
.crumb-year {
  color: var(--brand-color);
}
.separator {
  margin: 0 0.3em;
}
.top-line {
  display: flex;
  flex-wrap: wrap; /* narrow screens: the buttons go under the heading if they don't fit */
  align-items: baseline;
  justify-content: space-between;
  gap: 0.4rem 1rem;
}
.actions {
  display: flex;
  gap: 0.5rem;
}
/* the inverse of the Register Now pill (CallToAction/pill.css): same 17px radius, light
   background, dark text; slim top/bottom padding so it sits level with the "Year" heading */
.toggle-all {
  appearance: none;
  -webkit-appearance: none;
  margin: 0;
  padding: 0.15rem 0.85rem;
  border: none;
  border-radius: 17px;
  box-shadow: none;
  background: #e8e8e8;
  color: #1a1a1a;
  font: inherit;
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
}
.toggle-all:hover {
  background: white;
}
</style>
